<template>
    <el-form
        ref="registerForm"
        :model="form"
        :rules="rules"
        @keyup.enter.native="onSubmit"
    >
        <p class="orangetext" v-if="!isLogged">
            Create your account in order to submit your project for {{ currentAwardName }}.
            You can use the same account for all Global Design News awards submissions.
        </p>
        <p class="orangetext" v-else>
            You can edit your account changing basic information below. Remember this account is connected with all your
            past and future submissions to
            {{ currentAwardName }}.
        </p>
        <el-row :gutter="20">
            <el-col :sm="24" :md="12">
                <el-form-item prop="first_name" class="form-group" :error="$t(formErrors.get('first_name'))">
                    <InputWh
                        :placeholder="'first_name'"
                        v-model="form.first_name"
                        :required="rules['first_name'][0].required"
                    ></InputWh>
                </el-form-item>
            </el-col>
            <el-col :sm="24" :md="12">
                <el-form-item prop="last_name" class="form-group" :error="$t(formErrors.get('last_name'))">
                    <InputWh
                        :placeholder="'last_name'"
                        v-model="form.last_name"
                        :required="rules['first_name'][0].required"
                    ></InputWh>
                </el-form-item>
            </el-col>
        </el-row>
        <el-row :gutter="20">
            <el-col :sm="24" :md="12">
                <el-form-item prop="email" class="form-group" :error="$t(formErrors.get('email'))">
                    <InputWh
                        :placeholder="'email'"
                        v-model="form.email"
                        :required="rules['email'][0].required"
                    ></InputWh>
                </el-form-item>
            </el-col>
            <el-col :sm="24" :md="12">
                <el-form-item prop="phone" class="form-group" :error="$t(formErrors.get('phone'))">
                    <InputWh
                        :placeholder="'phone'"
                        v-model="form.phone"
                        :required="rules['phone'][0].required"
                    ></InputWh>
                </el-form-item>
            </el-col>
        </el-row>
        <el-row :gutter="20">
            <el-col :sm="24" :md="12">
                <el-form-item prop="password" class="form-group" :error="$t(formErrors.get('password'))"
                              :rules="[{
                               required: !isLogged, message: 'Password is required', trigger: 'blur'
                          },
                          {min: 8, message: 'Password should be at least 8 characters', trigger: 'blur'}
                        ]">
                    <el-input
                        v-model="form.password"
                        :placeholder="$t('auth.register.password_label')+'*'"
                        name="password"
                        show-password
                    />
                </el-form-item>
            </el-col>
            <el-col :sm="24" :md="12">
                <el-form-item prop="password_confirmation" class="form-group"
                              :rules="[
                            {required: !isLogged, message: 'Password Confirmation is required', trigger: 'blur'},
                            {validator: validatePassConfirmation, trigger:'blur'}
                        ]">
                    <el-input
                        :placeholder="$t('auth.register.password_confirmation_label')+'*'"
                        v-model="form.password_confirmation"
                        name="password_confirmation"
                        show-password
                    />
                </el-form-item>
            </el-col>
        </el-row>
        <el-row :gutter="20">
            <el-col :sm="24" :md="12">
                <el-form-item>
                    <el-checkbox v-model="form.newsletter">Subscribe to GDN mailing list</el-checkbox>
                </el-form-item>
            </el-col>
            <el-col :sm="24" :md="12">
                <el-form-item prop="privacy">
                    <el-checkbox v-model="form.privacy">I have read and agreed with the <a href="#" target="_blank">Privacy
                        Policy</a></el-checkbox>
                </el-form-item>
            </el-col>
        </el-row>
        <el-row>
            <el-col :sm="24" :md="12">
                &nbsp;
            </el-col>
            <el-col :sm="24" :md="12" class="text-right">
                <el-button
                    v-if="!isLogged"
                    :loading="loading"
                    @click.prevent="onSubmit"
                >
                    {{ $t('auth.register.submit_button') }}
                </el-button>
                <el-button
                    v-else
                    :loading="loading"
                    @click.prevent="onSubmit"
                >
                    {{ $t('auth.register.save') }}
                </el-button>
            </el-col>
        </el-row>
    </el-form>
</template>

<script>
import {Errors} from '../../../includes/Errors'
import InputWh from '@/core/components/InputWithHint'

export default {
    name: 'RegisterForm',
    props: {
        errors: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    components: {
        InputWh
    },
    data() {
        return {
            form: {
                first_name: '',
                last_name: '',
                phone: '',
                email: '',
                password: '',
                password_confirmation: '',
                newsletter: false,
                privacy: false
            },
            rules: {
                first_name: [
                    {required: true, message: 'First name is required', trigger: 'blur'},
                    {min: 3, max: 255, message: 'Length should be 3 to 255', trigger: 'blur'}
                ],
                last_name: [
                    {required: true, message: 'Last name is required', trigger: 'blur'},
                    {min: 3, max: 255, message: 'Length should be 3 to 255', trigger: 'blur'}
                ],
                phone: [
                    {required: true, message: 'Phone is required', trigger: 'blur'},
                    {min: 3, max: 255, message: 'Length should be 3 to 255', trigger: 'blur'}
                ],
                email: [
                    {required: true, message: 'Email is required', trigger: 'blur'},
                    {min: 3, max: 255, message: 'Length should be 3 to 255', trigger: 'blur'},
                    {type: 'email', message: 'Please input correct email address', trigger: 'blur'}
                ],
                privacy: [
                    {validator: this.checkPrivacy, trigger: 'blur'}
                ]
            },
            formErrors: new Errors()
        }
    },
    watch: {
        errors: function (val) {
            this.formErrors.record(this.errors)
        }
    },
    mounted: function () {
        let user = this.$auth.user();
        if (user) {
            this.form = JSON.parse(JSON.stringify(user));
        }
    },
    methods: {
        checkPrivacy(rule, value, callback) {
            if (!value) {
                callback(new Error('You must agree to the privacy policy'));
            } else {
                callback();
            }
        },
        validatePassConfirmation(rule, value, callback) {
            if (value && value !== this.form.password) {
                callback(new Error('Password and password confirmation must match'));
            } else {
                callback();
            }
        },
        onSubmit(e) {
            this.validateForm('registerForm').then((valid) => {
                this.$emit('submit', this.form)
            })
        },
        async validateForm(formName) {
            if (typeof this.formErrors !== 'undefined') {
                this.formErrors.clear(null)
            }
            return this.$refs[formName].validate()
        }
    }
}
</script>

<style lang="scss">

</style>
